import { useCallback, useMemo } from "react";
import {
  useGetNationalityList,
  useGetCompanySpecificIndustryList,
  useGetResidenceStatusList,
  useGetLanguageTestList,
} from "@/api/services/global/master";
import { useEnum } from "@/api/services/global/enum";
import { useGetUserListPersonInCharge } from "@/api/services/main/user";
import { useTalentFilterOptions } from "@/api/services/main/talent";
import { CaseMatchStatusOptions } from "@/types/enum";
import { truncateWithEllipsis } from "@/utils/string";
import { TOption } from "@/types/common";
import { FILTER_LABEL_MAX_LENGTH, FILTER_LABEL_FULL_WIDTH_MAX_LENGTH } from "@/features/talent/components/talent-list/talent-filter-shared";

export const useTalentFilterMasterData = () => {
  const { nationalityOptions } = useGetNationalityList();
  const { companySpecificIndustryOptions } = useGetCompanySpecificIndustryList();
  const { residenceStatusOptions } = useGetResidenceStatusList();
  const { languageTestLevelOptions } = useGetLanguageTestList();
  const { data: genderOptions } = useEnum("EGender");
  const { data: recruitmentStatusOptions } = useEnum("ERecruitmentStatus");
  const { data: educationLevelOptions } = useEnum("EEducationLevel");
  const { userOptions } = useGetUserListPersonInCharge();
  const { talentNumberOptions, fullNameOptions, nickNameOptions, desiredWorkLocationOptions } = useTalentFilterOptions();

  const truncateOpts = useCallback(
    (options: TOption[], maxLength = FILTER_LABEL_MAX_LENGTH) =>
      options.map((opt) => ({ ...opt, label: truncateWithEllipsis(opt.label, maxLength) })),
    [],
  );

  const truncatedOptions = useMemo(
    () => ({
      talentNumber: truncateOpts(talentNumberOptions, FILTER_LABEL_FULL_WIDTH_MAX_LENGTH),
      fullName: truncateOpts(fullNameOptions),
      nickName: truncateOpts(nickNameOptions),
      nationality: truncateOpts(nationalityOptions),
      gender: truncateOpts(genderOptions ?? []),
      recruitment: truncateOpts(recruitmentStatusOptions ?? []),
      residence: truncateOpts(residenceStatusOptions),
      language: truncateOpts(languageTestLevelOptions),
      industry: truncateOpts(companySpecificIndustryOptions),
      user: truncateOpts(userOptions),
      workLocation: truncateOpts(desiredWorkLocationOptions, FILTER_LABEL_FULL_WIDTH_MAX_LENGTH),
      caseMatchStatus: CaseMatchStatusOptions.map((opt) => ({
        value: String(opt.value),
        label: truncateWithEllipsis(opt.label, FILTER_LABEL_MAX_LENGTH),
      })),
      education: truncateOpts(
        (educationLevelOptions ?? []).map((opt) => ({ value: String(opt.value), label: opt.label })),
      ),
    }),
    [
      truncateOpts,
      talentNumberOptions,
      fullNameOptions,
      nickNameOptions,
      nationalityOptions,
      genderOptions,
      recruitmentStatusOptions,
      residenceStatusOptions,
      languageTestLevelOptions,
      companySpecificIndustryOptions,
      userOptions,
      desiredWorkLocationOptions,
      educationLevelOptions,
    ],
  );

  const rawOptions = useMemo(
    () => ({
      nationality: nationalityOptions,
      gender: genderOptions ?? [],
      recruitment: recruitmentStatusOptions ?? [],
      residence: residenceStatusOptions,
      language: languageTestLevelOptions,
      industry: companySpecificIndustryOptions,
      user: userOptions,
    }),
    [
      nationalityOptions,
      genderOptions,
      recruitmentStatusOptions,
      residenceStatusOptions,
      languageTestLevelOptions,
      companySpecificIndustryOptions,
      userOptions,
    ],
  );

  return { truncatedOptions, rawOptions };
};
